// utils/apiService.ts
import { useMsal } from "@azure/msal-react";
import { apiService as baseApiService } from "../../../api/apiService";
import { PublicClientApplication } from '@azure/msal-browser';

interface BusinessDescriptionData {
  management: string;
  function_l1: string;
  function_l2: string;
  clients: string;
  outputs: string;
  inputs: string;
  suppliers: string;
  processes: string;
  resources: string;
  objective_or_purpose: string;
}

interface KpiBasketData {
  indicator_name: string;
  what_does_it_measure: string;
  unit_of_measure: string;
  what_is_the_calculation_formula: string;
  check_items: string;
  who_is_responsible: string;
  is_it_related_to_any_global_goal: boolean;
  whichgoal: string;
  year: number;
  current_ytd_value: number;
  previous_fy_value: number;
  target_value: number;
}

interface CriticalTableData {
  output: string;
  reputational_external_impact_on: number;
  monthly_financial_impact: number;
  compliance_or_external_regulatory_affairs_impact: number;
  time_to_deliver_the_service: number;
  sum: number;
  critical: string;
}

interface FormStatus {
  bd_stage: string;
  job_hie_stage: string;
  kpi_basket_stage: string;
  critical_matrix_stage: string;
  sla_stage: string;
  [key: string]: string;
}

// Create a function that takes the instance as a parameter
const createApiCaller = (instance: PublicClientApplication) => ({
  createBusinessDescription: async (data: BusinessDescriptionData) => {
    return await baseApiService("/management/business_description", "POST", data, undefined, instance);
  },

  updateBusinessDescription: async (id: number, data: Partial<BusinessDescriptionData>) => {
    return await baseApiService(`/management/business_description/${id}`, "PATCH", data, undefined, instance);
  },

  uploadJobHierarchy: async (id: number, formData: FormData) => {
    return await baseApiService(`/management/job_hierarchy/${id}`, "POST", formData, undefined, instance);
  },

  submitKpiBasket: async (id: number, data: KpiBasketData[]) => {
    return await baseApiService(`/management/kpi_basket/${id}`, "POST", data, undefined, instance);
  },

  submitCriticalTable: async (id: number, data: CriticalTableData[]) => {
    return await baseApiService(`/management/critical_matrix/${id}`, "POST", data, undefined, instance);
  },

  uploadSLA: async (formData: FormData) => {
    return await baseApiService("/management/bd_sla", "POST", formData, undefined, instance);
  },

  submitBusinessDescription: async (id: number) => {
    return await baseApiService(`/management/submit_business_description/${id}`, "PATCH", undefined, undefined, instance);
  },

  fetchFormStatus: async (id: number): Promise<{ data: FormStatus }> => {
    return await baseApiService(`/management/business_description/${id}`, "GET", undefined, undefined, instance);
  },

  deleteDraft: async (id: number) => {
    return await baseApiService(`/management/business_description/${id}`, "DELETE", undefined, undefined, instance);
  },

  fetchBusinessDescriptionDetails: async (id: number) => {
    return await baseApiService(`/management/business_description/${id}`, "GET", undefined, undefined, instance);
  },
});

// Export a custom hook that provides the API functions
export const useApiService = () => {
  const { instance } = useMsal();
  return createApiCaller(instance as PublicClientApplication);
};